<template>
  <div class="import-container">
    <h5>学科竞赛信息上传</h5>

    <div class="upload-actions">
      <el-select
        v-model="selectedSemesterUuid"
        placeholder="请选择学期"
        class="control-select"
      >
        <el-option
          v-for="(semester, index) in semesters"
          :key="semester.uuid"
          :label="semester.name"
          :value="semester.uuid"
        >
          <span>{{ semester.name }}</span>
          <template v-if="index === 0">
            <el-tag type="primary" style="margin-left: 10px;">当前学期</el-tag>
          </template>
        </el-option>
      </el-select>

      <el-radio-group v-model="uploadType" class="identity-group">
        <el-radio label="学生">学生</el-radio>
        <el-radio label="教师">教师</el-radio>
        <el-radio label="班级">班级</el-radio>
      </el-radio-group>

      <el-select
        v-model="selectedTemplateUuid"
        placeholder="请选择奖状模板"
        class="control-select"
      >
        <el-option
          v-for="template in templates"
          :key="template.uuid"
          :label="template.name"
          :value="template.uuid"
        />
      </el-select>

      <el-upload
        class="upload-excel"
        ref="uploadRef"
        action=""
        :http-request="handleUpload"
        :before-upload="beforeUpload"
        :on-change="handleFileChange"
        :on-remove="handleRemove"
        accept=".xlsx, .xls"
        :auto-upload="false"
      >
        <template #trigger>
          <el-button type="primary">选择Excel文件</el-button>
        </template>
      </el-upload>

      <el-button
        type="success"
        @click="submitFile"
        :loading="loading"
      >
        上传到服务器
      </el-button>
    </div>

    <el-dialog
      v-model="previewDialogVisible"
      title="学科竞赛上传预览"
      width="85%"
      :close-on-click-modal="false"
    >
      <el-alert
        v-if="separatorErrors.length"
        class="separator-alert"
        title="检测到姓名分隔符不规范"
        type="error"
        show-icon
        :closable="false"
      >
        <template #default>
          获奖者或指导教师存在空格、逗号、分号、斜杠等非法分隔符，请使用“、”分隔多个姓名。
        </template>
      </el-alert>

      <el-table
        v-if="separatorErrors.length"
        :data="separatorErrors"
        border
        class="error-table"
        style="width:100%"
      >
        <el-table-column prop="sheetName" label="工作表" width="150" />
        <el-table-column prop="rowNumber" label="行号" width="90" />
        <el-table-column prop="field" label="字段" width="120" />
        <el-table-column prop="originalValue" label="错误值" min-width="180" show-overflow-tooltip />
        <el-table-column prop="fixedValue" label="程序修改值" min-width="180" show-overflow-tooltip />
      </el-table>

      <el-alert
        v-else-if="autoFixApplied"
        class="separator-alert"
        title="已应用程序修改，预览表格中的姓名分隔符已替换为“、”。"
        type="success"
        show-icon
        :closable="false"
      />

      <el-table
        :data="previewRows"
        border
        style="width:100%"
        :cell-class-name="previewCellClassName"
      >
        <el-table-column prop="Winner" label="获奖者" width="140" show-overflow-tooltip />
        <el-table-column prop="class" label="班级" width="120" show-overflow-tooltip />
        <el-table-column prop="AwardName" label="奖项名称" min-width="200" show-overflow-tooltip />
        <el-table-column prop="GradeName" label="奖项等级" width="150" show-overflow-tooltip />
        <el-table-column prop="mentor" label="指导教师" width="160" show-overflow-tooltip />
        <el-table-column prop="type" label="身份" width="100" />
      </el-table>

      <template #footer>
        <el-button
          v-if="separatorErrors.length"
          type="warning"
          @click="applyAutoFix"
        >
          应用程序修改
        </el-button>
        <el-button @click="cancelPreview">取消</el-button>
        <el-button
          type="success"
          :disabled="separatorErrors.length > 0"
          :loading="loading"
          @click="confirmUpload"
        >
          提交上传
        </el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-model="errorDialogVisible"
      title="错误信息"
      width="30%"
    >
      <span>{{ errorMessage }}</span>
      <template #footer>
        <el-button @click="errorDialogVisible = false">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import axios from 'axios'
import * as XLSX from 'xlsx'

const COLUMN_CANDIDATES = {
  Winner: ['获奖者', 'Winner', '获奖人'],
  class: ['班级', 'class'],
  AwardName: ['奖项名称', 'AwardName', '奖项名'],
  GradeName: ['奖项', 'GradeName', '奖项等级'],
  mentor: ['指导教师（可选）', '指导教师', 'mentor', '指导老师'],
}

const NAME_FIELD_CONFIGS = [
  { prop: 'Winner', label: '获奖者', candidates: COLUMN_CANDIDATES.Winner },
  { prop: 'mentor', label: '指导教师', candidates: COLUMN_CANDIDATES.mentor },
]

const ILLEGAL_SEPARATOR_REGEXP = /[\s,，;；/\\|]+/
const ALL_NAME_SEPARATOR_REGEXP = /[、\s,，;；/\\|]+/

export default {
  name: 'ImportPage',
  data() {
    return {
      file: null,
      uploadFile: null,
      workbook: null,
      previewSheetName: '',
      loading: false,
      semesters: [],
      templates: [],
      selectedSemesterUuid: '',
      selectedTemplateUuid: '',
      uploadType: '学生',
      errorDialogVisible: false,
      errorMessage: '',
      previewDialogVisible: false,
      previewRows: [],
      separatorErrors: [],
      autoFixApplied: false,
    }
  },
  mounted() {
    this.fetchSemesters()
    this.fetchTemplates()
  },
  methods: {
    async fetchSemesters() {
      try {
        const response = await axios.get('/api/semesters')
        this.semesters = response.data.semesters
      } catch (error) {
        console.error('获取学期数据失败:', error)
        ElMessage.error('获取学期数据失败')
      }
    },
    async fetchTemplates() {
      try {
        const res = await axios.get('/api/Competition/templates')
        this.templates = res.data.templates
      } catch (error) {
        console.error('获取模板列表失败:', error)
        ElMessage.error('获取模板列表失败')
      }
    },
    beforeUpload(file) {
      this.setSelectedFile(file)
      return false
    },
    handleFileChange(uploadFile) {
      this.setSelectedFile(uploadFile)
    },
    setSelectedFile(file) {
      const selectedFile = file && (file.raw || file)
      if (!selectedFile) {
        return
      }
      this.file = selectedFile
      this.uploadFile = selectedFile
      this.resetPreviewState()
    },
    handleRemove() {
      this.file = null
      this.uploadFile = null
      this.resetPreviewState()
    },
    handleUpload() {
      // 手动上传前先进入预览确认流程
    },
    async submitFile() {
      if (!this.file) {
        ElMessage.error('请先选择一个文件')
        return
      }
      if (!this.selectedSemesterUuid) {
        ElMessage.error('请选择一个学期')
        return
      }
      if (!this.uploadType) {
        ElMessage.error('请选择上传类型')
        return
      }
      if (!this.selectedTemplateUuid) {
        ElMessage.error('请选择一个奖状模板')
        return
      }

      this.loading = true
      try {
        await this.preparePreview()
        this.previewDialogVisible = true
      } catch (error) {
        this.errorMessage = error.message || '无法预览Excel文件'
        this.errorDialogVisible = true
      } finally {
        this.loading = false
      }
    },
    async preparePreview() {
      const workbook = await this.readWorkbook(this.file)
      this.workbook = workbook
      this.uploadFile = this.file
      this.previewRows = []
      this.separatorErrors = []
      this.autoFixApplied = false

      if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
        throw new Error('Excel文件没有包含任何工作表')
      }

      this.previewSheetName = workbook.SheetNames[0]
      const sheet = workbook.Sheets[this.previewSheetName]
      const { rows, headerIndex, headers, headerMap } = this.readSheetRows(sheet)
      if (headerIndex === -1) {
        throw new Error(`工作表 ${this.previewSheetName} 是空的`)
      }

      const requiredColumns = ['Winner', 'class', 'AwardName']
      const missingColumn = requiredColumns.find(prop => !this.findColumnInfo(headerMap, COLUMN_CANDIDATES[prop]))
      if (missingColumn) {
        throw new Error('Excel 缺少必要列：请确保包含 "获奖者", "班级", "奖项名称" 等列')
      }

      const dataRows = rows
        .slice(headerIndex + 1)
        .filter(rowInfo => this.hasRowContent(rowInfo.row))

      if (!dataRows.length) {
        throw new Error('Excel 文件中无数据')
      }

      this.previewRows = dataRows.map((rowInfo) => {
        const previewRow = this.mapPreviewRow(headers, headerMap, rowInfo)
        this.collectSeparatorErrors(headerMap, previewRow)
        return previewRow
      })
    },
    readWorkbook(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (event) => {
          try {
            const data = new Uint8Array(event.target.result)
            resolve(XLSX.read(data, { type: 'array', cellDates: false }))
          } catch (error) {
            reject(new Error(`无法读取Excel文件: ${error.message}`))
          }
        }
        reader.onerror = () => reject(new Error('无法读取Excel文件'))
        reader.readAsArrayBuffer(file)
      })
    },
    readSheetRows(sheet) {
      const rows = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: '',
        raw: false,
      }).map((row, index) => ({
        row,
        rowNumber: index + 1,
      }))

      const headerIndex = rows.findIndex(rowInfo => this.hasRowContent(rowInfo.row))
      if (headerIndex === -1) {
        return { rows, headerIndex, headers: [], headerMap: {} }
      }

      const headers = rows[headerIndex].row.map(value => this.cellToText(value).trim())
      const headerMap = headers.reduce((result, header, index) => {
        if (header) {
          result[header] = {
            index,
            cellAddress: XLSX.utils.encode_cell({ r: headerIndex, c: index }),
          }
        }
        return result
      }, {})

      return { rows, headerIndex, headers, headerMap }
    },
    mapPreviewRow(headers, headerMap, rowInfo) {
      const row = rowInfo.row
      return {
        __rowNumber: rowInfo.rowNumber,
        __invalidFields: {},
        Winner: this.getRowValue(row, headerMap, COLUMN_CANDIDATES.Winner),
        class: this.getRowValue(row, headerMap, COLUMN_CANDIDATES.class),
        AwardName: this.getRowValue(row, headerMap, COLUMN_CANDIDATES.AwardName),
        GradeName: this.getRowValue(row, headerMap, COLUMN_CANDIDATES.GradeName),
        mentor: this.getRowValue(row, headerMap, COLUMN_CANDIDATES.mentor),
        type: this.uploadType,
      }
    },
    collectSeparatorErrors(headerMap, previewRow) {
      NAME_FIELD_CONFIGS.forEach((field) => {
        const columnInfo = this.findColumnInfo(headerMap, field.candidates)
        if (!columnInfo) {
          return
        }

        const checkResult = this.getNameSeparatorFix(previewRow[field.prop])
        if (!checkResult.invalid) {
          return
        }

        previewRow.__invalidFields[field.prop] = true
        this.separatorErrors.push({
          id: `${this.previewSheetName}-${previewRow.__rowNumber}-${field.label}`,
          sheetName: this.previewSheetName,
          previewProp: field.prop,
          rowNumber: previewRow.__rowNumber,
          field: field.label,
          originalValue: checkResult.originalValue,
          fixedValue: checkResult.fixedValue,
          cellAddress: XLSX.utils.encode_cell({ r: previewRow.__rowNumber - 1, c: columnInfo.index }),
        })
      })
    },
    getNameSeparatorFix(value) {
      const originalValue = this.cellToText(value).trim()
      if (!originalValue || originalValue.toLowerCase() === 'nan') {
        return { invalid: false, originalValue, fixedValue: originalValue }
      }

      const parts = originalValue
        .split(ALL_NAME_SEPARATOR_REGEXP)
        .map(item => item.trim())
        .filter(Boolean)

      if (parts.length <= 1 || !ILLEGAL_SEPARATOR_REGEXP.test(originalValue)) {
        return { invalid: false, originalValue, fixedValue: originalValue }
      }

      return {
        invalid: true,
        originalValue,
        fixedValue: parts.join('、'),
      }
    },
    applyAutoFix() {
      this.separatorErrors.forEach((error) => {
        const sheet = this.workbook.Sheets[error.sheetName]
        if (sheet && error.cellAddress) {
          sheet[error.cellAddress] = {
            t: 's',
            v: error.fixedValue,
            w: error.fixedValue,
          }
        }

        const row = this.previewRows.find(item => item.__rowNumber === error.rowNumber)
        if (row) {
          row[error.previewProp] = error.fixedValue
          row.__invalidFields[error.previewProp] = false
        }
      })

      const fixedWorkbook = XLSX.write(this.workbook, {
        bookType: 'xlsx',
        type: 'array',
      })
      const fixedFileName = this.getFixedFileName(this.file.name)
      this.uploadFile = new File([fixedWorkbook], fixedFileName, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      this.separatorErrors = []
      this.autoFixApplied = true
      ElMessage.success('已应用程序修改')
    },
    async confirmUpload() {
      if (this.separatorErrors.length > 0) {
        ElMessage.error('请先应用程序修改，或取消后修改原文件')
        return
      }

      this.loading = true
      const formData = new FormData()
      formData.append('file', this.uploadFile || this.file)
      formData.append('semesterUuid', this.selectedSemesterUuid)
      formData.append('uploadType', this.uploadType)
      formData.append('templateUuid', this.selectedTemplateUuid)

      try {
        const response = await axios.post('/api/competition/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        ElMessage.success(response.data.message || '文件上传成功')
        this.resetUploadState()
      } catch (error) {
        this.errorMessage = error.response?.data?.error || '文件上传失败'
        this.errorDialogVisible = true
      } finally {
        this.loading = false
      }
    },
    cancelPreview() {
      this.previewDialogVisible = false
    },
    resetUploadState() {
      this.file = null
      this.uploadFile = null
      this.selectedSemesterUuid = ''
      this.selectedTemplateUuid = ''
      this.uploadType = '学生'
      this.previewDialogVisible = false
      this.resetPreviewState()
      if (this.$refs.uploadRef) {
        this.$refs.uploadRef.clearFiles()
      }
    },
    resetPreviewState() {
      this.workbook = null
      this.previewSheetName = ''
      this.previewRows = []
      this.separatorErrors = []
      this.autoFixApplied = false
    },
    previewCellClassName({ row, column }) {
      return row.__invalidFields && row.__invalidFields[column.property]
        ? 'invalid-name-cell'
        : ''
    },
    findColumnInfo(headerMap, candidates) {
      const matchedHeader = candidates.find(header => headerMap[header])
      return matchedHeader ? headerMap[matchedHeader] : null
    },
    getRowValue(row, headerMap, candidates) {
      const columnInfo = this.findColumnInfo(headerMap, candidates)
      return columnInfo ? this.cellToText(row[columnInfo.index]).trim() : ''
    },
    hasRowContent(row) {
      return row.some(value => this.cellToText(value).trim() !== '')
    },
    cellToText(value) {
      return value === null || value === undefined ? '' : String(value)
    },
    getFixedFileName(fileName) {
      if (!fileName) {
        return 'fixed-competition-upload.xlsx'
      }
      return fileName.toLowerCase().endsWith('.xlsx')
        ? fileName
        : fileName.replace(/\.[^.]+$/, '') + '.xlsx'
    },
  },
}
</script>

<style scoped>
.import-container {
  text-align: center;
}

.upload-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.control-select {
  width: 220px;
}

.identity-group {
  min-width: 190px;
}

.separator-alert {
  margin-bottom: 12px;
}

.error-table {
  margin-bottom: 16px;
}

:deep(.invalid-name-cell) {
  background-color: #fff2f0;
  color: #c45656;
  font-weight: 600;
}
</style>